import {STUser} from "api/organisationApi/types";
import {STAuthResponse} from "api/types";
import axios, {
	AxiosError,
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";
import createAuthRefreshInterceptor, {AxiosAuthRefreshRequestConfig} from "axios-auth-refresh";
import config from "config";
import {createBroadcastChannel} from "helpers/BroadcastChannel";
import {BROADCAST_CHANNELS} from "helpers/BroadcastChannel/BroadcastChannels";
import {getItem, setItem} from "helpers/localStorage";
import {Mixpanel} from "helpers/mixpanel";
import {disconnectPusher} from "web-socket/pusherClient";

// ─── Memory-only access token ─────────────────────────────────────────────
// Never written to localStorage — XSS scripts cannot read it.

let memoryToken: string | null = null;

// Resolves once the boot-time token refresh completes. The request interceptor
// awaits this before sending any request, so no API call fires before we have
// a valid Bearer token — zero 401s in the network tab on page reload.
export let bootRefreshPromise: Promise<void> | null = null;

export function setMemoryToken(token: string): void {
	memoryToken = token;
}

export function clearMemoryToken(): void {
	memoryToken = null;
}

export function getMemoryToken(): string | null {
	return memoryToken;
}

// ─── Shared axios instance ────────────────────────────────────────────────
// One instance shared by all HttpClient subclasses.
// This is what eliminates the per-instance refresh race (Bug 1).

const sharedAxiosInstance: AxiosInstance = axios.create({
	baseURL: config.apiUrl,
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
});

sharedAxiosInstance.interceptors.request.use(async (reqConfig: InternalAxiosRequestConfig) => {
	// Queue this request until the boot refresh resolves. After the first
	// page load this promise is null and the await is a no-op.
	if (bootRefreshPromise) {
		await bootRefreshPromise;
	}

	if (memoryToken && reqConfig.headers) {
		reqConfig.headers.Authorization = `Bearer ${memoryToken}`;
	}

	return reqConfig;
});

// ─── Logout ───────────────────────────────────────────────────────────────

const ERROR_PAGES = [
	"/error",
	"/unknown-error",
	"/500",
	"/404",
	"/403",
	"/503",
	"/invitation-invalid",
];

// Redux `isAuthenticated` (see AppContainer/PrivateRoute) is only reset by a full
// page reload today. This event lets Redux clear its auth state immediately,
// independent of whether/when window.location.href actually navigates away —
// otherwise a delayed or missed reload leaves PrivateRoute rendering private
// routes on every client-side navigation while every request keeps 401ing.
export const AUTH_LOGOUT_EVENT = "mapx:auth-logout";

let isLoggingOut = false;

function handleSharedLogout(): void {
	if (isLoggingOut) return;
	isLoggingOut = true;

	disconnectPusher();
	window.dispatchEvent(new Event(AUTH_LOGOUT_EVENT));

	let redirect = "/login";
	const pathname = window.location.pathname;

	if (pathname && !ERROR_PAGES.includes(pathname)) {
		redirect += `?redirectTo=${pathname}`;
	}

	const savedUser = getItem("user");
	const user: STUser | null = savedUser ? JSON.parse(savedUser) : null;

	if (user?.email) {
		Mixpanel.track("logged out", {distinct_id: user.email});
		Mixpanel.reset();
	}

	window.localStorage.clear();
	window.location.href = redirect;
}

// ─── Token helpers ────────────────────────────────────────────────────────

export function storeTokens({access_token, refresh_token, expires_in}: STAuthResponse): void {
	setMemoryToken(access_token);
	setItem("expires_in", String(Date.now() + expires_in * 1000));
	setItem("refresh_token", refresh_token);
}

const {postMessage: postAuthBroadcast} = createBroadcastChannel<STAuthResponse>(
	BROADCAST_CHANNELS.AUTH_TOKEN,
);

function broadcastTokens(data: STAuthResponse): void {
	postAuthBroadcast(data);
}

// Uses a plain axios call (not sharedAxiosInstance) to avoid recursive interception.
// Module-level promise deduplicates concurrent callers so they share one HTTP request.
let activeRefreshPromise: Promise<STAuthResponse> | null = null;

function callRefreshEndpoint(): Promise<STAuthResponse> {
	// Once logout has started (e.g. the refresh token itself was rejected), localStorage
	// is cleared. Without this guard, other requests that 401 around the same time would
	// trigger a second refresh call with an empty payload, producing a spurious 400.
	if (isLoggingOut) {
		return Promise.reject(new Error("Logout in progress"));
	}

	if (activeRefreshPromise) return activeRefreshPromise;

	// Bare axios is used (not sharedAxiosInstance) to avoid recursive interception.
	const storedRefreshToken = getItem("refresh_token");

	activeRefreshPromise = axios
		.post<STAuthResponse>(
			"/auth/refresh",
			storedRefreshToken ? {refresh_token: storedRefreshToken} : {},
			{
				baseURL: config.apiUrl as string,
			},
		)
		.then((res) => res.data)
		.finally(() => {
			activeRefreshPromise = null;
		});

	return activeRefreshPromise;
}

// Only used to classify failures of the /auth/refresh call itself. Any definitive 4xx
// (401/403 invalid token, but also 400 when the backend rejects a missing/malformed
// refresh_token) means retrying won't help — log out. Network errors (no response) are
// excluded so the interceptor keeps retrying automatically on the next API call.
function isAuthFailure(error: unknown): boolean {
	return (
		axios.isAxiosError(error) &&
		error.response != null &&
		error.response.status >= 400 &&
		error.response.status < 500
	);
}

// ─── axios-auth-refresh interceptor ──────────────────────────────────────
// Called once per 401. The library queues all in-flight 401 requests and
// retries them with the new token after this function resolves.

type AuthErrorData = {detail?: string};

// Auth paths that must never trigger token refresh on 401.
// All other /auth/ endpoints (including new 2FA endpoints) are refreshable by default.
const REFRESH_DENIED_AUTH_PATHS = ["/auth/login", "/auth/logout", "/auth/register"];

function refreshAuthLogic(failedRequest: AxiosError): Promise<void> {
	const url = failedRequest.config?.url ?? "";
	const responseData = failedRequest.response?.data as AuthErrorData | undefined;

	const isDeniedPath = REFRESH_DENIED_AUTH_PATHS.some((p) => url.startsWith(p));
	const isBlockedByMessage =
		responseData?.detail === "Invalid 2FA code" || responseData?.detail === "Invalid password";

	if (isDeniedPath || isBlockedByMessage) {
		return Promise.reject(failedRequest);
	}

	return callRefreshEndpoint()
		.then((data) => {
			storeTokens(data);
			broadcastTokens(data);
		})
		.catch((error: unknown) => {
			// Only logout on 4xx auth failure. Network errors keep the user logged in —
			// the interceptor will retry automatically on the next API call.
			if (isAuthFailure(error)) {
				handleSharedLogout();
			}

			return Promise.reject(error);
		});
}

createAuthRefreshInterceptor(sharedAxiosInstance, refreshAuthLogic, {
	deduplicateRefresh: true,
	// Mark every request that was queued during a refresh cycle as non-refreshable
	// when it is released. Without this, a queued request that happens to fail after
	// the refresh would bypass deduplication (which is cleared by then) and trigger
	// a second refresh cycle — consuming the newly-issued refresh token.
	onRetry: (requestConfig) => {
		(requestConfig as AxiosAuthRefreshRequestConfig).skipAuthRefresh = true;

		return requestConfig;
	},
});

// ─── HttpClient ───────────────────────────────────────────────────────────

class HttpClient {
	private client: AxiosInstance = sharedAxiosInstance;

	// Optional per-instance base URL. Subclasses targeting a different domain
	// (e.g. the Polaris API) pass it here; it is applied per request so the
	// shared axios instance still handles auth + token refresh, while requests
	// reach the right host. Token refresh itself always targets config.apiUrl.
	private readonly baseURLOverride?: string;

	constructor(baseURLOverride?: string) {
		this.baseURLOverride = baseURLOverride;
	}

	private withBaseURL(config: AxiosRequestConfig = {}): AxiosRequestConfig {
		return this.baseURLOverride ? {baseURL: this.baseURLOverride, ...config} : config;
	}

	public setAccessToken(token: string): void {
		setMemoryToken(token);
	}

	public hasExpired(): boolean {
		const expiresIn = getItem("expires_in");
		const tokenExpiryTime = expiresIn ? parseInt(expiresIn, 10) : null;

		return tokenExpiryTime !== null && tokenExpiryTime <= Date.now();
	}

	public hasToken(): boolean {
		return memoryToken !== null;
	}

	async doGet<T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
		return this.client
			.get<T>(url, this.withBaseURL(config))
			.then((res: AxiosResponse<T>) => res)
			.catch((err) => err?.response);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async doPost<T, P extends Record<string, any>>(
		url: string,
		body?: P,
		config?: AxiosRequestConfig,
		hasMultimedia = false,
	): Promise<AxiosResponse> {
		const finalConfig = hasMultimedia
			? {
					...config,
					headers: {
						...config?.headers,
						"Content-Type": "multipart/form-data",
					},
			  }
			: config;

		const formData = hasMultimedia ? this.toFormData(body) : body;

		return this.client
			.post<T>(url, formData, this.withBaseURL(finalConfig))
			.then((res: AxiosResponse<T>) => res)
			.catch((err) => err?.response);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async doPatch<T, P extends Record<string, any>>(
		url: string,
		body?: P,
		config: AxiosRequestConfig = {},
	): Promise<AxiosResponse<T>> {
		return this.client
			.patch<T>(url, body, this.withBaseURL(config))
			.then((res: AxiosResponse<T>) => res)
			.catch((err) => err?.response);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async doPut<T, P extends Record<string, any>>(
		url: string,
		body?: P,
		config: AxiosRequestConfig = {},
	): Promise<AxiosResponse<T>> {
		return this.client
			.put<T>(url, body, this.withBaseURL(config))
			.then((res: AxiosResponse<T>) => res)
			.catch((err) => err?.response);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async doDelete<T, P extends Record<string, any>>(
		url: string,
		body?: P,
		config?: AxiosRequestConfig,
	): Promise<AxiosResponse<T>> {
		return this.client
			.delete<T>(url, {data: body, ...this.withBaseURL(config)})
			.then((res: AxiosResponse<T>) => res)
			.catch((err) => err?.response);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private toFormData(body?: Record<string, any>): FormData {
		const formData = new FormData();

		if (body) {
			Object.entries(body).forEach(([key, value]) => {
				if (typeof value !== "function") {
					formData.append(key, value as string);
				}
			});
		}

		return formData;
	}
}

// ─── Boot initialization ─────────────────────────────────────────────────────
// Runs synchronously when the module first loads — before any React component
// mounts. Starts the token refresh so the request interceptor can queue all
// outgoing requests until we have a valid Bearer token.
const _bootRefreshToken = getItem("refresh_token");

if (!memoryToken && _bootRefreshToken) {
	bootRefreshPromise = callRefreshEndpoint()
		.then((data) => {
			storeTokens(data);
			broadcastTokens(data);
		})
		.catch((error: unknown) => {
			if (isAuthFailure(error)) {
				handleSharedLogout();
			}
		})
		.finally(() => {
			bootRefreshPromise = null;
		});
}

export default HttpClient;
